import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { RuntimeConfig } from '../config';

export interface HostAdminUser {
  readonly email: string;
  readonly name: string | null;
}

/** Sign-in for the teachpartner-host realm. OIDC (code + PKCE) or a local mock. */
export interface AuthService {
  currentUser(): Promise<HostAdminUser | null>;
  accessToken(): Promise<string | null>;
  signIn(): Promise<void>;
  /** Completes the redirect on /auth/callback. */
  completeSignIn(): Promise<HostAdminUser>;
  signOut(): Promise<void>;
}

export const CALLBACK_PATH = '/auth/callback';
/** Matches central-api's mock verifier (HOST_AUTH_MODE=mock, refused in production). */
export const MOCK_BEARER = ['mock', 'host', 'admin'].join('-');
const MOCK_KEY = 'tp.hostAdmin.mockSignedIn';

export function mockAuth(storage: Storage = sessionStorage): AuthService {
  const user: HostAdminUser = { email: 'host.admin@example.com', name: 'Mock Host Admin' };
  const signedIn = () => storage.getItem(MOCK_KEY) === '1';
  return {
    currentUser: async () => (signedIn() ? user : null),
    accessToken: async () => (signedIn() ? MOCK_BEARER : null),
    signIn: async () => storage.setItem(MOCK_KEY, '1'),
    completeSignIn: async () => {
      storage.setItem(MOCK_KEY, '1');
      return user;
    },
    signOut: async () => storage.removeItem(MOCK_KEY),
  };
}

interface OidcSettings {
  readonly oidcIssuer: string;
  readonly oidcClientId: string;
}

function createManager(config: OidcSettings, origin: string): UserManager {
  return new UserManager({
    authority: config.oidcIssuer,
    client_id: config.oidcClientId,
    redirect_uri: `${origin}${CALLBACK_PATH}`,
    post_logout_redirect_uri: origin,
    response_type: 'code',
    scope: 'openid email profile',
    // Session storage only: credentials never outlive the tab.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
  });
}

export function oidcAuth(
  config: OidcSettings,
  origin: string = window.location.origin,
  manager: UserManager = createManager(config, origin),
): AuthService {
  const toUser = (profile: { email?: string; name?: string }): HostAdminUser => ({
    email: profile.email ?? '',
    name: profile.name ?? null,
  });
  const live = async () => {
    const user = await manager.getUser();
    return user && !user.expired ? user : null;
  };
  return {
    currentUser: async () => {
      const user = await live();
      return user ? toUser(user.profile) : null;
    },
    accessToken: async () => (await live())?.access_token ?? null,
    signIn: () => manager.signinRedirect(),
    completeSignIn: async () => toUser((await manager.signinRedirectCallback()).profile),
    signOut: () => manager.signoutRedirect(),
  };
}

export function createAuth(config: RuntimeConfig): AuthService {
  return config.authMode === 'oidc' ? oidcAuth(config) : mockAuth();
}
